import { useEffect, useRef, useState, useCallback, type ReactNode } from 'react'
import { cn } from '@/lib/utils'

type ScrollViewProps = {
  children: ReactNode
  className?: string
  style?: React.CSSProperties
  trigger?: any
  theme?: 'light' | 'dark'
  direction?: 'horizontal' | 'vertical'
  enhance?: boolean
  radius?: string
}

export default function ScrollView({
  children,
  className,
  style,
  trigger,
  theme: _theme = 'light',
  direction = 'horizontal',
  enhance = false,
  radius,
}: ScrollViewProps) {
  const [isAtStart, setIsAtStart] = useState(true)
  const [isAtEnd, setIsAtEnd] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)

  const updateScrollState = useCallback(() => {
    if (scrollRef.current) {
      if (direction === 'horizontal') {
        const { scrollWidth, clientWidth, scrollLeft } = scrollRef.current
        setIsAtStart(scrollLeft <= 0)
        setIsAtEnd(scrollLeft + clientWidth >= scrollWidth)
      } else {
        const { scrollHeight, clientHeight, scrollTop } = scrollRef.current
        setIsAtStart(scrollTop <= 0)
        setIsAtEnd(scrollTop + clientHeight >= scrollHeight)
      }
    }
  }, [direction])

  useEffect(() => {
    let ticking = false

    const throttledUpdate = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          updateScrollState()
          ticking = false
        })
        ticking = true
      }
    }

    const handleResize = () => {
      throttledUpdate()
    }

    updateScrollState()

    window.addEventListener('resize', handleResize, { passive: true })

    const ref = scrollRef.current
    if (ref) ref.addEventListener('scroll', throttledUpdate, { passive: true })

    return () => {
      window.removeEventListener('resize', handleResize)
      if (ref) ref.removeEventListener('scroll', throttledUpdate)
    }
  }, [trigger, updateScrollState])

  const getMaskImage = () => {
    const fadeSize = enhance ? '48px' : '16px'

    if (direction === 'horizontal') {
      const leftMask = isAtStart ? '' : `linear-gradient(to right, transparent, black ${fadeSize})`
      const rightMask = isAtEnd ? '' : `linear-gradient(to left, transparent, black ${fadeSize})`

      if (!isAtStart && !isAtEnd) {
        return `${leftMask}, ${rightMask}`
      }
      return leftMask || rightMask || 'none'
    } else {
      const topMask = isAtStart ? '' : `linear-gradient(to bottom, transparent, black ${fadeSize})`
      const bottomMask = isAtEnd ? '' : `linear-gradient(to top, transparent, black ${fadeSize})`

      if (!isAtStart && !isAtEnd) {
        return `${topMask}, ${bottomMask}`
      }
      return topMask || bottomMask || 'none'
    }
  }

  const maskImage = getMaskImage()
  const maskComposite = !isAtStart && !isAtEnd ? 'intersect' : 'add'
  // WebKit uses different keywords than the standard maskComposite
  const webkitMaskComposite = maskComposite === 'intersect' ? 'source-in' : 'source-over'

  return (
    <div className={cn('hide-scrollbar relative max-w-full flex-1 overflow-hidden', radius)}>
      <div
        ref={scrollRef}
        className={cn(
          'relative w-full',
          direction === 'horizontal' ? 'overflow-x-auto' : 'overflow-y-auto',
          className,
        )}
        style={{
          maskImage,
          WebkitMaskImage: maskImage,
          maskComposite,
          WebkitMaskComposite: webkitMaskComposite,
          ...style,
        }}
      >
        {children}
      </div>
    </div>
  )
}
